/** Closed set the login page and the API log agree on (QL-637). */
export const SSO_SIGN_IN_ERROR_KINDS = [
  "cancelled",
  "expired",
  "idp_rejected_request",
  "idp_unavailable",
  "idp_timeout",
  "token_not_verified",
  "missing_user_info",
  "email_required",
  "provider_misconfigured",
  "account_not_linked",
  "signup_disabled",
  "account_unavailable",
  "last_password_admin",
  "identity_changed",
  "link_email_sent",
  "unknown",
] as const;
export type SsoSignInErrorKind = (typeof SSO_SIGN_IN_ERROR_KINDS)[number];

export const SSO_ERROR_DESCRIPTION_MAX_CHARS = 300;
/** `code` is IdP-controlled too; sanitised the same way, capped shorter (it is a short token, not a message). */
export const SSO_ERROR_CODE_MAX_CHARS = 100;

/**
 * Unicode format characters (category Cf): the bidi override/isolate/mark
 * characters a crafted `error_description` or `code` could use to visually
 * reorder the plain text an admin reads ("Trojan Source", CVE-2021-42574 class),
 * plus zero-width space/joiners, word joiner, soft hyphen and BOM, which hide
 * text or split a known code so it no longer classifies. Stripped, not replaced
 * with a space, since they carry no word-boundary meaning.
 */
const FORMAT_CHARS = /\p{Cf}/gu;

/**
 * NFC, bidi controls stripped, C0/C1 control chars and line/paragraph separators
 * turned into a space (so a multi-line IdP message still reads as separate words
 * instead of gluing them together), whitespace collapsed, trimmed. Shared core of
 * `sanitizeSsoErrorDescription` and `sanitizeSsoErrorCode` (both IdP-controlled).
 */
function stripSsoErrorText(raw: string): string {
  return (
    raw
      .normalize("NFC")
      .replace(FORMAT_CHARS, "")
      // biome-ignore lint/suspicious/noControlCharactersInRegex: turning control/line-separator chars into a space is the point
      .replace(/[\u0000-\u001f\u007f-\u009f\u2028\u2029]/g, " ")
      .replace(/\s+/g, " ")
      .trim()
  );
}

/** `text` cut to `max` UTF-16 units without leaving half of a surrogate pair at the end. */
function capText(text: string, max: number): string {
  return text.slice(0, max).replace(/[\ud800-\udbff]$/, "");
}

/** Plain text only: sanitised, capped at 300 chars with a trailing ellipsis, or null when empty. */
export function sanitizeSsoErrorDescription(raw: unknown): string | null {
  if (typeof raw !== "string") return null;
  const text = stripSsoErrorText(raw);
  if (!text) return null;
  return text.length > SSO_ERROR_DESCRIPTION_MAX_CHARS
    ? `${capText(text, SSO_ERROR_DESCRIPTION_MAX_CHARS - 1)}…`
    : text;
}

/** Cap for the identity provider's message shown to the end user on the login page (QL-637). */
export const SSO_ERROR_LOGIN_MESSAGE_MAX_CHARS = 200;
/** What a removed link/domain becomes in the login-page message: language-neutral. */
export const SSO_ERROR_LINK_PLACEHOLDER = "[…]";

/**
 * `scheme://…`, the `javascript:`/`data:`/`mailto:`-style schemes, `www.…`, and the
 * scheme-less `host.tld` / `host.tld/path` forms; not the domain of an e-mail.
 */
const LINK_LIKE =
  /\b[a-z][a-z0-9+.-]*:\/\/\S*|\b(?:javascript|data|vbscript|mailto|tel|sms|file):\S+|\bwww\.\S*|(?<![@\w.-])(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z]{2,}(?::\d+)?(?:[/?#]\S*)?/gi;
/** A bare plugin/OAuth token (`token_not_verified`, `access_denied`): a code, not a message from anyone. */
const BARE_TOKEN = /^[a-z0-9]+(?:_[a-z0-9]+)+$/i;

/**
 * The identity provider's `error_description` as the END USER may see it on the
 * login page (QL-637, PO decision 2026-10-20). It arrives in the sign-in URL, so
 * anyone can write it: sanitised like every description (plain text, control,
 * bidi and other format characters stripped), every link or bare domain replaced
 * with `[…]` so it can't steer the user anywhere, and capped at 200 chars. Null
 * when empty, when only links remain, or when it is just a code (the kind's own
 * message already says what it means).
 */
export function sanitizeSsoErrorMessageForLogin(raw: unknown): string | null {
  const text = sanitizeSsoErrorDescription(raw);
  if (!text || BARE_TOKEN.test(text)) return null;
  const unlinked = text.replace(LINK_LIKE, SSO_ERROR_LINK_PLACEHOLDER).replace(/\s+/g, " ").trim();
  if (!unlinked.replace(/\[…\]|[\s.,:;!?()-]/g, "")) return null;
  return unlinked.length > SSO_ERROR_LOGIN_MESSAGE_MAX_CHARS
    ? `${capText(unlinked, SSO_ERROR_LOGIN_MESSAGE_MAX_CHARS - 1)}…`
    : unlinked;
}

/** Same sanitising as the description, capped at 100 chars, never null (an empty `code` stays ""). */
export function sanitizeSsoErrorCode(raw: string): string {
  return capText(stripSsoErrorText(raw), SSO_ERROR_CODE_MAX_CHARS);
}

/** A reference the login page may render: the UUID the API minted, nothing else. */
export function isSsoSignInErrorRef(value: unknown): value is string {
  return (
    typeof value === "string" &&
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value)
  );
}

/** Whether `value` is one of the closed `SsoSignInErrorKind` set — the only thing `?ssoKind=` may select. */
export function isSsoSignInErrorKind(value: unknown): value is SsoSignInErrorKind {
  return (
    typeof value === "string" && (SSO_SIGN_IN_ERROR_KINDS as readonly string[]).includes(value)
  );
}

/**
 * `code`/`description` normalised for comparison: format characters (bidi
 * controls, zero-width characters) stripped (so a contaminated known code still
 * classifies instead of falling through to "unknown" — the raw `code` passed in
 * here hasn't been through `sanitizeSsoErrorCode` yet), trimmed, lower-cased,
 * `_`↔space collapsed to space.
 */
function normalise(value: string): string {
  return value
    .replace(FORMAT_CHARS, "")
    .trim()
    .toLowerCase()
    .replace(/[_\s]+/g, " ");
}

const CANCELLED_CODES = new Set(
  [
    "access_denied",
    "login_required",
    "consent_required",
    "interaction_required",
    "account_selection_required",
  ].map(normalise),
);
const EXPIRED_CODES = new Set(
  [
    "invalid_state",
    "state_mismatch",
    // better-auth: the callback carried no state / an undecryptable cookie state.
    "state_not_found",
    "state_invalid",
    // @better-auth/sso: the provider was edited while the sign-in was in flight.
    "sso_provider_changed",
    "please_restart_the_process",
    "internal_server_error",
  ].map(normalise),
);
const IDP_UNAVAILABLE_CODES = new Set(["server_error", "temporarily_unavailable"].map(normalise));
const IDP_REJECTED_CODES = new Set(
  [
    "invalid_request",
    "unauthorized_client",
    "unsupported_response_type",
    "invalid_scope",
    "invalid_client",
    "invalid_grant",
  ].map(normalise),
);
const PROVIDER_MISCONFIGURED_CODES = new Set(
  ["discovery_failed", "unsupported_token_auth_method"].map(normalise),
);
/**
 * `invalid_provider` also wraps a failed token request: @better-auth/sso puts the
 * IdP's `error_description`, else the HTTP status text, else its OAuth `error`
 * into the description. A rejected client (expired or wrong secret, Entra
 * AADSTS7000xxx) or grant is the IdP rejecting the request, not a stale QP
 * configuration; a 5xx status text is the IdP being unavailable.
 */
const TOKEN_REJECTED_DESCRIPTION =
  /^(invalid client|invalid grant|unauthorized client|unauthorized|bad request)$|\binvalid client\b|\binvalid grant\b|\bclient secret\b|\baadsts7000\d+/;
const TOKEN_IDP_UNAVAILABLE_DESCRIPTION =
  /^(internal server error|bad gateway|service unavailable|gateway timeout)$/;

const ACCOUNT_NOT_LINKED_CODES = new Set(
  ["account not linked", "unable to link account", "account_tenant_mismatch"].map(normalise),
);

/**
 * Maps the (code, description) pair a failed SSO sign-in produced to the closed
 * presentation kind the login page and the API log agree on (QL-637). First
 * match wins — see the spec's classification table for the source of each row.
 */
const INVALID_PROVIDER = normalise("invalid_provider");
const INVALID_STATE = normalise("invalid_state");
const SSO_TIMEOUT = normalise("sso_timeout");
const EMAIL_REQUIRED = normalise("email_required");
const SIGNUP_DISABLED = normalise("signup disabled");
const USER_DELETED = normalise("user_deleted");
const LAST_PASSWORD_ADMIN = normalise("last_password_admin");
const SSO_IDENTITY_CHANGED = normalise("sso_identity_changed");
const LINK_EMAIL_SENT = normalise("link_email_sent");
const TOKEN_NOT_VERIFIED = normalise("token_not_verified");
const MISSING_USER_INFO = normalise("missing_user_info");

export function classifySsoSignInError(
  code: string | null | undefined,
  description?: string | null,
): SsoSignInErrorKind {
  const c = normalise(code ?? "");
  const d = description != null ? normalise(description) : "";

  if (c === LINK_EMAIL_SENT) return "link_email_sent";
  if (CANCELLED_CODES.has(c)) return "cancelled";
  if (c === INVALID_STATE) return "expired"; // any description
  if (EXPIRED_CODES.has(c)) return "expired";
  if (c === SSO_TIMEOUT) return "idp_timeout";
  if (IDP_UNAVAILABLE_CODES.has(c)) return "idp_unavailable";
  if (IDP_REJECTED_CODES.has(c)) return "idp_rejected_request";
  if (c === INVALID_PROVIDER && d === TOKEN_NOT_VERIFIED) return "token_not_verified";
  if (c === INVALID_PROVIDER && d === MISSING_USER_INFO) return "missing_user_info";
  if (c === INVALID_PROVIDER && TOKEN_REJECTED_DESCRIPTION.test(d)) return "idp_rejected_request";
  if (c === INVALID_PROVIDER && TOKEN_IDP_UNAVAILABLE_DESCRIPTION.test(d)) return "idp_unavailable";
  if (c === INVALID_PROVIDER) return "provider_misconfigured";
  if (PROVIDER_MISCONFIGURED_CODES.has(c)) return "provider_misconfigured";
  if (c === EMAIL_REQUIRED) return "email_required";
  if (ACCOUNT_NOT_LINKED_CODES.has(c)) return "account_not_linked";
  if (c === SIGNUP_DISABLED) return "signup_disabled";
  if (c === USER_DELETED) return "account_unavailable";
  if (c === LAST_PASSWORD_ADMIN) return "last_password_admin";
  if (c === SSO_IDENTITY_CHANGED) return "identity_changed";
  return "unknown";
}
